// Phase 43 — useCanvasNav hook (Plan 43-04).
// IntersectionObserver-backed scroll-spy for the Live-Fill Canvas section nav,
// plus a programmatic-scroll guard so jumpTo() doesn't race the IO callback.
// See 43-RESEARCH.md "Research Area 2" (lines 489-560) + 43-PATTERNS.md §29.
//
// Contracts (Plan 43-04 Task 1 behavior block):
// - activeSection tracks the highest intersectionRatio entry.
// - jumpTo(id) sets active immediately + arms a 600ms suppression window so
//   the IO callback doesn't flicker back to a sibling while smooth-scroll
//   transitions.
// - Reduced-motion media-query collapses the window to 0ms (no smooth
//   scroll, no need to suppress).
// - observer.disconnect() on unmount.

import { useCallback, useEffect, useRef, useState } from 'react';

export function useCanvasNav(sectionIds: readonly string[]) {
  const [activeSection, setActiveSection] = useState<string>(
    sectionIds[0] ?? '',
  );

  // ref-based guard so the IO callback can read the latest suppression state
  // without depending on render cycles.
  const programmaticTargetRef = useRef<string | null>(null);
  const programmaticTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return; // SSR / JSDOM guard

    const observer = new IntersectionObserver(
      (entries) => {
        // Suppress during programmatic scroll window (Pitfall #2 race).
        if (programmaticTargetRef.current) return;

        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;

        const top = visible.reduce((a, b) =>
          a.intersectionRatio >= b.intersectionRatio ? a : b,
        );
        const id = (top.target as Element).id;
        if (id) setActiveSection(id);
      },
      {
        // Top ~20% ignored (header), middle ~10% is the active band, bottom
        // 70% ignored — this is the standard scroll-spy rootMargin.
        rootMargin: '-20% 0px -70% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sectionIds]);

  const jumpTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    programmaticTargetRef.current = id;
    setActiveSection(id);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (programmaticTimeoutRef.current) {
      clearTimeout(programmaticTimeoutRef.current);
    }
    programmaticTimeoutRef.current = setTimeout(
      () => {
        programmaticTargetRef.current = null;
      },
      reducedMotion ? 0 : 600,
    );
  }, []);

  // Cleanup pending timeout on unmount so no stale writes fire after tear-down.
  useEffect(() => {
    return () => {
      if (programmaticTimeoutRef.current) {
        clearTimeout(programmaticTimeoutRef.current);
      }
    };
  }, []);

  return { activeSection, jumpTo };
}
